import { ReactElement } from 'react';

import { HIDE_CONTAINER_HEADER_CLASSNAME } from '@components/hide-container/hide-container';
import { SidebarReadOnlyContext, showSidebar } from '@components/sidebar';

import { getJourneyActionQueryKey } from 'api/journey-actions/hooks';
import { queryClient } from 'api/query-client';
import { TEMPLATE_PREVIEW_BUTTON_CLASSNAME } from 'ui/actions/common';

import { JourneyNodeBase } from './node-types/types';
import { setContinueRegistry } from './unsaved-changes-prompt';

type SidebarComponent = (props: { journeyActionId: string }) => ReactElement;
export type SidebarContentMap = Record<string, SidebarComponent>;

let sidebarContentMapRef: SidebarContentMap | null = null;

export const registerSidebarContentMap = (map: SidebarContentMap) => {
  sidebarContentMapRef = map;
};

export const showActionSidebar = (node: JourneyNodeBase, options?: { isNewAction?: boolean }) => {
  if (!sidebarContentMapRef) {
    console.error('Sidebar content map not registered');
    return;
  }

  const hasSidebar = node.type in sidebarContentMapRef;
  if (hasSidebar) {
    const SidebarComponent = sidebarContentMapRef[node.type];
    setContinueRegistry({ onContinue: () => showActionSidebar(node), id: node.id });
    queryClient.removeQueries(getJourneyActionQueryKey(node.id));
    showSidebar({
      component: SidebarComponent,
      options: {
        variant: 'REGULAR',
        id: node.id,
        onHide: () => {
          setTimeout(() => {
            queryClient.invalidateQueries({
              predicate: (query) =>
                query.queryKey[0] === 'journey' && query.queryKey[1] !== 'action',
            });
            // let closing animation finish before invalidating queries
          }, 350);
        },
      },
      props: { journeyActionId: node.id, ...(options?.isNewAction ? { isNewAction: true } : {}) },
    });
  }
};

/**
 * Show action sidebar in read-only mode for version preview.
 * Similar to showTemplateActionSidebar but for preview mode.
 */
export const showPreviewActionSidebar = (node: JourneyNodeBase) => {
  if (!sidebarContentMapRef) {
    console.error('Sidebar content map not registered');
    return;
  }

  const hasSidebar = node.type in sidebarContentMapRef;
  if (hasSidebar) {
    const SidebarComponent = sidebarContentMapRef[node.type];
    showSidebar({
      component: (props) => (
        <SidebarReadOnlyContext.Provider value={true}>
          <SidebarComponent {...props} />
        </SidebarReadOnlyContext.Provider>
      ),
      options: {
        variant: 'REGULAR',
        id: node.id,
        containerStyle: {
          '#sidebar-footer': {
            display: 'none',
          },
          [`.${HIDE_CONTAINER_HEADER_CLASSNAME}, .${TEMPLATE_PREVIEW_BUTTON_CLASSNAME}`]: {
            zIndex: 1,
          },
        },
      },
      props: { journeyActionId: node.id },
    });
  }
};
